
import React from 'react';
import { ArcStack, ArcCellInfo, ArcButton, ArcText, ArcSwitch } from '@src/core/ui';
import { useDispatch } from 'react-redux';
import {
  setSelectedZone,
  useDeleteSafeLandZoneMutation,
  useUpdateSafeLandZoneMutation,
} from '@src/features/zones/redux';
import ZonePopupWrapper from '@src/features/zones/components/zone-popup-wrapper';
import { useNavigate } from 'react-router-dom';
import { routesConfig } from '@src/core/routes';
import { toast } from 'react-toastify';
import { arcToFixed } from '@src/core/extensions';
import { ZoneEntity } from '@src/core/features';
import { ZoneDetailsSafeLandPopupProps } from './types';

const ZoneDetailsPopupSafeLand: React.FC<ZoneDetailsSafeLandPopupProps> = (props) => {
  const { zone = {} as ZoneEntity } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [deleteZone] = useDeleteSafeLandZoneMutation();
  const [updateZone] = useUpdateSafeLandZoneMutation();

  const getZoneInfo = () => {
    return {
      id: zone?.id || "N/A",
      name: zone?.name || "N/A",
      description: zone?.description || "N/A",
      locationName: zone?.locationName || "N/A",
      altitudeMeters: zone?.altitudeMeters ?? 0,
      isActive: zone?.isActive || false,
    };
  };

  const zoneInfo = getZoneInfo();

  const onToggleActive = async () => {
    if (!zone?.id) return;
    try {
      await updateZone({
        id: zone.id,
        name: zone.name,
        description: zone.description,
        centerLatitude: zone.centerLatitude,
        centerLongitude: zone.centerLongitude,
        altitudeMeters: zone.altitudeMeters ?? 0,
        locationName: zone.locationName,
        isActive: !zone.isActive,
      }).unwrap();
      toast.success(`Zone ${zone.isActive ? 'deactivated' : 'activated'} successfully`);
      onClose();
    } catch {
      toast.error(`Failed to ${zone?.isActive ? 'deactivate' : 'activate'} zone`);
    }
  };

  const onClose = () => {
    dispatch(setSelectedZone(null));
  };

  const onEditClick = () => {
    navigate(`${routesConfig.editSafeLandZone}/${zone?.id}`);
    onClose();
  };

  const onDeleteClick = async () => {
    try {
      await deleteZone({ id: zone?.id ?? 0 }).unwrap();
      toast.success('Zone deleted successfully');
      onClose();
      navigate(routesConfig.zones);
    } catch (error) {
      toast.error('Failed to delete zone');
    }
  };

  if (!zone?.id) {
    return null;
  }

  return (
    <ZonePopupWrapper title="ZONE DETAILS">
      <ArcStack spacing={2}>
        <ArcStack>
          <ArcStack direction="row" spacing={4} marginTop={1}>
            <ArcCellInfo label="Name" textProps={{ sx: { wordBreak: 'break-word' } }} flex={1}>
              {zoneInfo.name}
            </ArcCellInfo>

            <ArcCellInfo
              label="Description"
              textProps={{ sx: { wordBreak: 'break-word' }, fontWeight: 400 }} flex={1}
            >
              {zoneInfo.description}
            </ArcCellInfo>

          </ArcStack>
        </ArcStack>
        <ArcStack direction="row" spacing={4}>
          <ArcCellInfo label="Altitude" textProps={{ sx: { wordBreak: 'break-word' } }} flex={1}>
            {arcToFixed(zoneInfo.altitudeMeters)} m
          </ArcCellInfo>

          <ArcCellInfo label="Location" textProps={{ fontWeight: 400 }} flex={1}>
            {zoneInfo.locationName}
          </ArcCellInfo>
        </ArcStack>

        <ArcStack>
          <ArcStack direction="row" spacing={1} justifyContent="space-between" mt={1}>
            <ArcButton.ghost fullWidth onClick={onEditClick} size="large">
          Edit
            </ArcButton.ghost>
            <ArcButton.ghost fullWidth onClick={onDeleteClick} size="large">
          Delete
            </ArcButton.ghost>
          </ArcStack>
        </ArcStack>
        <ArcStack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <ArcText.button textTransform="none" fontSize={15} fontWeight={400}>
          Activate/Deactivate zone
          </ArcText.button>
          <ArcSwitch.brand
            checked={zone?.isActive}
            onChange={onToggleActive}
            sx={{
              '& .MuiSwitch-track': { backgroundColor: 'background.default' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: 'background.default',
              },
            }}
          />
        </ArcStack>
      </ArcStack>
    </ZonePopupWrapper>
  );



};

export default ZoneDetailsPopupSafeLand;